import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaMapMarkerAlt, FaChevronDown, FaClock, FaFire } from 'react-icons/fa';
import { getPopularLocalities, submitSearchQuery } from '../services/api';
import { useNavigate } from 'react-router-dom';
import PropertyImage from './PropertyImage';

const CITIES = ['Mumbai', 'Pune', 'Bengaluru', 'Hyderabad', 'Ahmedabad', 'Chennai', 'Delhi', 'Kolkata', 'Thane', 'Goa'];

/* ── Recently Viewed helpers ── */
const RECENT_KEY = 'mb_recent_properties';
const getRecentlyViewed = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
const addRecentlyViewed = (property) => {
  const prev = getRecentlyViewed().filter(p => p.slug !== property.slug);
  const updated = [{ slug: property.slug, title: property.title, location: property.location, price: property.price, images: property.images, builderName: property.builderName, _id: property._id }, ...prev].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
};

const Hero = ({ selectedCity = 'Mumbai' }) => {
  const navigate = useNavigate();
  const [propertyType, setPropertyType] = useState('Apartment');
  const [popularLocalities, setPopularLocalities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [defaultResults, setDefaultResults] = useState([]);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [recentResults, setRecentResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Property Types Configuration
  const PROPERTY_TYPES = [
    // { value: 'Bungalow', label: 'Bungalow', icon: '🏡' },
    { value: 'Plot', label: 'Plot' },
    { value: 'Commercial', label: 'Commercial'},
  ];

  // City selector state
  const [heroCity, setHeroCity] = useState(selectedCity);
  const [cityDropOpen, setCityDropOpen] = useState(false);
  const cityDropRef = useRef(null);
  const searchBoxRef = useRef(null);

  // Sync heroCity with selectedCity prop
  useEffect(() => { setHeroCity(selectedCity); }, [selectedCity]);

  // Close city dropdown & search on outside click
  useEffect(() => {
    const handler = (e) => {
      if (cityDropRef.current && !cityDropRef.current.contains(e.target)) {
        setCityDropOpen(false);
      }
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load recently viewed from localStorage
  useEffect(() => {
    setRecentResults(getRecentlyViewed());
  }, []);

  const fetchDefaultProperties = useCallback(async () => {
    if (defaultLoading || defaultResults.length > 0) return;
    setDefaultLoading(true);
    try {
      const results = await submitSearchQuery('');
      setDefaultResults(Array.isArray(results) ? results : []);
    } catch (err) {
      console.error('Failed to fetch default properties:', err);
    } finally {
      setDefaultLoading(false);
    }
  }, [defaultLoading, defaultResults.length]);

  // Fetch popular localities when heroCity or propertyType changes (no redirect)
  useEffect(() => {
    getPopularLocalities(heroCity.toLowerCase(), 6, propertyType).then(data => {
      if (Array.isArray(data)) setPopularLocalities(data);
    }).catch(err => console.error('Failed to fetch popular localities:', err));
  }, [heroCity, propertyType]);

  // Filter results based on selected property type
  const filterResultsByPropertyType = useCallback((results) => {
    if (!results || results.length === 0) return [];
    
    // Map property types to backend categories
    const typeMapping = {
      'Apartment': ['Apartment', 'RESIDENTIAL'],
      'Bungalow': ['Villa', 'Bungalow', 'Rowhouse', 'RESIDENTIAL'],
      'Plot': ['Plot', 'PLOT'],
      'Commercial': ['Commercial', 'Office', 'Retail', 'COMMERCIAL']
    };

    const allowedCategories = typeMapping[propertyType] || [];
    
    return results.filter(property => {
      const category = property.category || property.propertyType || '';
      return allowedCategories.some(type => 
        category.toLowerCase().includes(type.toLowerCase())
      );
    });
  }, [propertyType]);

  const handleSearch = useCallback(async (query) => {
    setSearchLoading(true);
    try {
      const results = await submitSearchQuery(query);
      const allResults = Array.isArray(results) ? results : [];
      
      // Filter by selected property type
      const filteredResults = filterResultsByPropertyType(allResults);
      
      setSearchResults(filteredResults.slice(0, 8));
      setShowSearchResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [filterResultsByPropertyType]);

  // Handle search with debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, handleSearch]);

  const handleSelectProperty = (property) => {
    addRecentlyViewed(property);
    setRecentResults(getRecentlyViewed());
    const slug = property.slug || property._id;
    setSearchQuery('');
    setShowSearchResults(false);
    navigate(`/projects/${slug}`);
  };

  const handleLocalityClick = (localityObj) => {
    const slug = typeof localityObj === 'object' ? localityObj.slug : localityObj.toLowerCase().replace(/\s+/g, '-');
    const city = heroCity.toLowerCase();
    navigate(`/in/${city}/${slug}`);
  };

  // Build the dropdown list: recently viewed first (with label), then all defaults filtered
  const buildDropdownSections = () => {
    const recentSlugs = new Set(recentResults.map(r => r.slug));
    const freshDefaults = defaultResults.filter(p => !recentSlugs.has(p.slug));
    return { recentResults, freshDefaults };
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Redirect to search results page with query
      const queryToSearch = searchQuery.trim();
      setShowSearchResults(false);
      setSearchQuery('');
      navigate(`/search?q=${encodeURIComponent(queryToSearch)}`);
    }
  };

  // Property card renderer shared by dropdown sections
  const PropertyItem = ({ property, isRecent }) => (
    <button
      onMouseDown={(e) => { e.preventDefault(); handleSelectProperty(property); }}
      type="button"
      className="w-full px-4 py-2.5 hover:bg-gray-50 transition flex items-start space-x-3 text-left"
    >
      <PropertyImage
        property={property}
        className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden"
        imgClassName="w-full h-full object-cover"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {isRecent && <FaClock className="text-gray-300 text-[10px] flex-shrink-0" />}
          <h4 className="text-sm font-semibold text-gray-900 truncate">{property.title}</h4>
        </div>
        {property.builderName && <p className="text-xs text-gray-400 truncate">by {property.builderName}</p>}
        {property.location && (
          <p className="text-xs text-gray-500 flex items-center mt-0.5">
            <FaMapMarkerAlt className="text-red-400 mr-1 text-[9px]" />{property.location}
          </p>
        )}
        {property.price && <p className="text-xs font-bold text-red-500 mt-0.5">₹{property.price}</p>}
      </div>
    </button>
  );

  return (
    <section className="relative min-h-[400px] bg-gradient-to-br from-[#0f0c29] via-[#1a1a2e] to-[#302b63] pt-20 z-[50]">
      {/* Decorative elements — clipped independently so dropdowns can overflow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-6 animate-fadeInUp"> 
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-sm text-gray-300">{CITIES[0]} Real Estate</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Buy Homes <span className="text-red-500">Directly</span>
              <br />
              With <span className="text-red-500">Builders</span>
            </h1>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <span className="text-green-400 text-lg">✓</span>
                </div>
                <span className="text-lg font-medium">No Brokerage</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <span className="text-yellow-400 text-lg">★</span>
                </div>
                <span className="text-lg font-medium">Bottom Rate</span>
              </div>
            </div>
          </div>

          {/* Right - Search Box */}
          <div className="relative z-[49]">
            {/* Search Box */}
            <div className="bg-white rounded-2xl p-4 shadow-2xl max-w-xl relative z-[100]">
              {/* Property Type Tabs + City Selector */}
              <div className="flex items-center space-x-2 mb-4 flex-wrap gap-2">
                {['Apartment'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setPropertyType(type)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      propertyType === type
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                ))}
                 {PROPERTY_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setPropertyType(type.value);
                      setSearchQuery(''); // Clear search when changing type
                      setSearchResults([]);
                      setShowSearchResults(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                      propertyType === type.value
                        ? 'bg-red-500 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-red-400 hover:text-red-500'
                    }`}
                  >
                    <span className="text-lg">{type.icon}</span>
                    <span>{type.label}</span>
                  </button>
                ))}

                {/* Dynamic City Selector */}
                <div ref={cityDropRef} className="relative ml-auto">
                  <button
                    onClick={() => setCityDropOpen(v => !v)}
                    className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:border-red-400 hover:text-red-500 transition"
                  >
                    <FaMapMarkerAlt className="text-red-500 text-xs" />
                    <span>{heroCity}</span>
                    <FaChevronDown className={`text-xs text-gray-400 transition-transform ${cityDropOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {cityDropOpen && (
                    <div className="absolute top-full right-0 mt-1.5 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-[200]">
                      {CITIES.map(c => (
                        <button
                          key={c}
                          onClick={() => {
                            setHeroCity(c);
                            setCityDropOpen(false);
                            // No redirect — localities update via useEffect
                          }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-red-50 hover:text-red-500 transition ${
                            heroCity === c ? 'text-red-500 font-semibold bg-red-50' : 'text-gray-700'
                          }`}
                        >
                          <FaMapMarkerAlt className="text-xs opacity-60" />
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Search Input */}
              <form onSubmit={handleSearchSubmit} className="relative">
                <div ref={searchBoxRef} className="relative">
                  <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3">
                    <FaSearch className="text-gray-400 mr-3" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => {
                        setShowSearchResults(true);
                        fetchDefaultProperties();
                      }}
                      placeholder={`Search ${propertyType.toLowerCase()}s in ${heroCity}...`}
                      className="w-full bg-transparent text-gray-700 text-sm outline-none placeholder-gray-400"
                    />
                    <button 
                      type="submit"
                      className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ml-2"
                    >
                      Search
                    </button>
                  </div>

                  {/* Search Dropdown */}
                  {showSearchResults && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-[10000] max-h-[420px] overflow-y-auto">
                      {searchLoading && (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500" />
                        </div>
                      )}

                      {/* Typed search results */}
                      {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length > 0 && (
                        <>
                          <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Search Results</div>
                          {searchResults.map(p => <PropertyItem key={p._id || p.slug} property={p} isRecent={false} />)}
                        </>
                      )}

                      {/* No results for typed query */}
                      {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                        <p className="text-center text-sm text-gray-400 py-6">No results found</p>
                      )}

                      {/* Default suggestions (shown when no query typed) */}
                      {!searchLoading && searchQuery.trim().length < 2 && (() => {
                        const { recentResults: recent, freshDefaults } = buildDropdownSections();
                        return (
                          <>
                            {recent.length > 0 && (
                              <>
                                <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                  <FaClock className="text-[9px]" /> Recently Viewed
                                </div>
                                {recent.map(p => <PropertyItem key={p.slug} property={p} isRecent={true} />)}
                              </>
                            )}
                            {freshDefaults.length > 0 && (
                              <>
                                <div className="px-4 pt-3 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                  <FaFire className="text-red-400 text-[9px]" /> Suggested Properties
                                </div>
                                {freshDefaults.map(p => <PropertyItem key={p._id || p.slug} property={p} isRecent={false} />)}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {searchLoading && searchQuery.trim().length < 2 && null}
                </div>
              </form>

              {/* Popular Localities */}
              <div className="mt-3 flex items-center flex-wrap gap-2">
                <span className="text-xs text-gray-500 font-medium">Popular for {propertyType}s:</span>
                {popularLocalities.length > 0 ? (
                  popularLocalities.map((loc) => (
                    <button
                      key={loc.slug || loc.name}
                      onClick={() => handleLocalityClick(loc)}
                      className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full cursor-pointer hover:bg-red-50 hover:text-red-500 transition"
                    >
                      {loc.name}
                    </button>
                  ))
                ) : (
                  <span className="text-xs text-gray-400">Loading...</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave Bottom */}
      {/* <div className="absolute hidden sm:flex bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 80L60 68C120 56 240 32 360 24C480 16 600 24 720 32C840 40 960 48 1080 44C1200 40 1320 24 1380 16L1440 8V80H0Z" fill="white"/>
        </svg>
      </div> */}
    </section>
  );
};

export default Hero;
